import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, hasCapability } from "@/lib/permissions";
import BBSLogo from "@/components/BBSLogo";
import AssetIQLogo from "@/components/AssetIQLogo";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, Wrench, BarChart3, Users, LogOut, Menu, Bell, KeyRound, ShoppingCart, Boxes, ClipboardCheck, ClipboardList, ArrowRightLeft, Moon, Sun, FileText, ShieldCheck, Store, Sparkles, Building2, PenLine, Mail, AlertTriangle, LayoutGrid, Timer, RefreshCw, Truck, UsersRound as UsersIcon, User,
} from "lucide-react";
import NotificationBell from "@/components/NotificationBell";
import MySessionsDialog from "@/components/MySessionsDialog";
import SignatureDialog from "@/components/SignatureDialog";
import WelcomeTour from "@/components/WelcomeTour";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Feb 2026 — Sidebar is now driven by capabilities (from user.effective_capabilities)
// instead of hardcoded role arrays. This means when an admin toggles a permission
// in Settings → Roles, the sidebar link appears/disappears immediately for that
// role — no code change required. Previously the sidebar had a separate `roles: [...]`
// list which drifted from the permissions matrix and caused bugs like
// "Accounts Assistant granted view_consumables but Consumables link doesn't show".
//
// Each entry declares either:
//   • `cap: "view_consumables"`  — user must have this single capability
//   • `capsAny: ["cap_a","cap_b"]` — user must have AT LEAST ONE of these
// If neither is set, the link is visible to any authenticated user.
//
// Sep 2026 — sidebar is grouped into sections. `section: "assetiq" | "peopleiq" | "admin"`
// controls which group header the link renders under. Defaults to "assetiq".
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true, capsAny: ["view_assets", "view_pos", "view_maintenance"], section: "assetiq" },
  { to: "/assets", label: "Asset Registry", icon: Package, testid: "nav-assets", cap: "view_assets", section: "assetiq" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart, testid: "nav-po", cap: "view_pos", section: "assetiq" },
  { to: "/vendors", label: "Vendors", icon: Store, testid: "nav-vendors", cap: "view_vendors", section: "assetiq" },
  { to: "/consumables", label: "Consumables", icon: Boxes, testid: "nav-consumables", cap: "view_consumables", section: "assetiq" },
  { to: "/consumable-requests", label: "Requisitions", icon: ClipboardList, testid: "nav-requisitions",
    capsAny: ["raise_consumable_request", "approve_consumable_request"], section: "assetiq" },
  { to: "/material-transfers", label: "Material Transfers", icon: ArrowRightLeft, testid: "nav-transfers",
    capsAny: ["raise_material_transfer", "gate_in_material_transfer", "receive_material_transfer"], section: "assetiq" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance", cap: "view_maintenance", section: "assetiq" },
  { to: "/maintenance/kanban", label: "Breakdown Kanban", icon: LayoutGrid, testid: "nav-kanban", cap: "view_kanban", section: "assetiq" },
  { to: "/maintenance/external-repairs", label: "External Repairs", icon: Truck, testid: "nav-external-repairs",
    capsAny: ["create_external_repair", "gate_out_external_repair", "gate_in_external_repair",
              "approve_external_repair_estimate", "close_external_repair"], section: "assetiq" },
  { to: "/breakdown-report", label: "Report Breakdown", icon: AlertTriangle, testid: "nav-breakdown-report", cap: "report_breakdown", section: "assetiq" },
  { to: "/audits", label: "Physical Audit", icon: ClipboardCheck, testid: "nav-audits", cap: "run_audit", section: "assetiq" },
  { to: "/notifications", label: "Notifications", icon: Bell, testid: "nav-notifications", section: "assetiq" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", cap: "view_reports", section: "assetiq" },
  // ---------- People IQ — its own module section ----------
  { to: "/people", label: "Employees", icon: UsersIcon, testid: "nav-people", cap: "view_employees", section: "peopleiq" },
  { to: "/people/directory", label: "Directory", icon: LayoutGrid, testid: "nav-directory", cap: "view_employees", section: "peopleiq" },
  { to: "/people/approvals", label: "Approval Queue", icon: ClipboardList, testid: "nav-people-approvals", cap: "approve_employee_change_request", section: "peopleiq" },
  { to: "/people/templates", label: "Checklists", icon: ClipboardCheck, testid: "nav-people-templates", cap: "manage_checklist_templates", section: "peopleiq" },
  { to: "/people/me", label: "My Profile", icon: User, testid: "nav-my-profile", section: "peopleiq" },
];

const SECTION_META = {
  assetiq: { label: "Asset IQ", testid: "section-assetiq" },
  peopleiq: { label: "People IQ", testid: "section-peopleiq" },
  admin: { label: "Administration", testid: "section-admin" },
};

export default function Layout() {
  const { user, logout, refresh } = useAuth();
  const [syncingCaps, setSyncingCaps] = useState(false);
  const syncCapabilities = async () => {
    setSyncingCaps(true);
    try {
      await refresh?.();
      toast.success("Session refreshed — new permissions are now active");
    } catch {
      toast.error("Refresh failed — try logging out and back in");
    } finally {
      setSyncingCaps(false);
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);

  const nav = NAV.filter((n) => {
    if (n.cap) return hasCapability(user, n.cap);
    if (Array.isArray(n.capsAny) && n.capsAny.length) {
      return n.capsAny.some((c) => hasCapability(user, c));
    }
    return true; // no capability requirement → visible to any authenticated user
  });
  if (hasCapability(user, "manage_users")) {
    nav.push({ to: "/users", label: "User Management", icon: Users, testid: "nav-users", section: "admin" });
  }
  if (hasCapability(user, "manage_company_settings")) {
    nav.push({ to: "/company-settings", label: "Company Settings", icon: Building2, testid: "nav-company-settings", section: "admin" });
  }
  if (hasCapability(user, "manage_sla_config")) {
    nav.push({ to: "/settings/sla", label: "Breakdown SLA", icon: Timer, testid: "nav-sla-settings", section: "admin" });
  }
  if (hasCapability(user, "manage_asset_name_options")) {
    nav.push({ to: "/asset-name-options", label: "Asset-Name Dropdowns", icon: Package, testid: "nav-asset-name-options", section: "admin" });
  }
  if (hasCapability(user, "manage_email_templates")) {
    nav.push({ to: "/email-templates", label: "Email Configuration", icon: Mail, testid: "nav-email-templates", section: "admin" });
  }

  // Group nav items by section, preserving insertion order.
  const sectionOrder = ["assetiq", "peopleiq", "admin"];
  const grouped = sectionOrder
    .map((key) => ({ key, meta: SECTION_META[key], items: nav.filter((n) => (n.section || "assetiq") === key) }))
    .filter((g) => g.items.length > 0);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static top-0 left-0 h-screen w-64 bg-white border-r border-slate-200
          flex flex-col z-40 transition-transform duration-200
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
        `}
      >
        <div className="px-5 py-5 border-b border-slate-100">
          <BBSLogo size={80} />
          <div className="mt-3">
            <AssetIQLogo height={54} />
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto" data-testid="sidebar-nav">
          {grouped.map((group) => (
            <div key={group.key} className="space-y-1">
              <div
                data-testid={group.meta.testid}
                className="px-3 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 select-none"
              >
                {group.meta.label}
              </div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testid}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `
                    flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors
                    ${isActive
                      ? "bg-[#fcf0f3] text-[#7C1D3A]"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}
                  `}
                >
                  <item.icon className="w-4 h-4" strokeWidth={2} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-100">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md hover:bg-slate-50 text-left transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#7C1D3A] text-white flex items-center justify-center font-heading font-semibold text-sm shrink-0">
                  {user?.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
                  <div className="text-xs text-slate-500 truncate">{ROLE_LABELS[user?.role]}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-normal text-xs text-slate-500">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setPwdOpen(true)}
                data-testid="change-password-menuitem"
                className="cursor-pointer"
              >
                <KeyRound className="w-4 h-4 mr-2" />
                Change password
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSessionsOpen(true)}
                data-testid="my-sessions-menuitem"
                className="cursor-pointer"
              >
                <ShieldCheck className="w-4 h-4 mr-2" />
                My sessions
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={syncCapabilities}
                disabled={syncingCaps}
                data-testid="sync-caps-menuitem"
                className="cursor-pointer"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${syncingCaps ? "animate-spin" : ""}`} />
                Sync my permissions
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setSigOpen(true)}
                data-testid="my-signature-menuitem"
                className="cursor-pointer"
              >
                <PenLine className="w-4 h-4 mr-2" />
                My signature
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setTourOpen(true)}
                data-testid="tour-menuitem"
                className="cursor-pointer"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Take the tour
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/synopsis")}
                data-testid="synopsis-menuitem"
                className="cursor-pointer"
              >
                <FileText className="w-4 h-4 mr-2" />
                App synopsis (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="logout-button"
                className="text-red-600 focus:text-red-600 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-8 h-16 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 -ml-2 text-slate-600"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-toggle"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="font-heading text-lg font-semibold text-slate-900 hidden sm:block">
              {nav.find((n) => location.pathname === n.to || (n.to !== "/" && location.pathname.startsWith(n.to)))?.label || "Asset IQ"}
            </h1>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <DarkModeToggle />
            <NotificationBell />
            <div className="text-xs text-slate-500 hidden md:block">
              <span className="font-mono">{new Date().toLocaleDateString("en-IN", {
                day: "2-digit", month: "long", year: "numeric", timeZone: "Asia/Kolkata",
              })} IST</span>
            </div>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-8">
          <Outlet />
        </main>
      </div>

      <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />
      <MySessionsDialog open={sessionsOpen} onOpenChange={setSessionsOpen} />
      <SignatureDialog open={sigOpen} onOpenChange={setSigOpen} />
      <WelcomeTour forceOpen={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
}

function DarkModeToggle() {
  const [dark, setDark] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem("assetiq_theme");
    if (stored) return stored === "dark";
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  const toggle = () => {
    const next = !dark;
    setDark(next);
    localStorage.setItem("assetiq_theme", next ? "dark" : "light");
    document.documentElement.classList.toggle("dark", next);
  };
  // Sync on mount
  if (typeof document !== "undefined") {
    document.documentElement.classList.toggle("dark", dark);
  }
  return (
    <button
      onClick={toggle}
      className="p-2 rounded-md hover:bg-slate-100 transition-colors"
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      data-testid="dark-mode-toggle"
    >
      {dark ? <Sun className="w-5 h-5 text-amber-500" /> : <Moon className="w-5 h-5 text-slate-700" />}
    </button>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      onOpenChange(false);
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update password");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Change Password</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
          <div>
            <Label>Current password</Label>
            <Input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password" />
          </div>
          <div>
            <Label>New password (min 8 chars)</Label>
            <Input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={submitting} className="bg-[#7C1D3A] hover:bg-[#6c1c36]" data-testid="save-password-btn">
              {submitting ? "Updating..." : "Update Password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
